import { COMMAND_CENTER_ROOT, commandCenterPath } from './commandCenterRoutes'
import { useNavigate } from 'react-router-dom'
import { ConferenceCalls, useConferenceCallStatus, COMMS_ROOT, TEAM_ROOT } from './comms'
import { useConferenceCalls, createConferenceCall, cancelConferenceCall, conferenceRoomPath } from './comms/conferenceCallsData'
import type { Meeting } from '../../api/client'

/**
 * V2 Conference Calls — route wrapper (PHASE_2M D4: WIRE_DAILY_CONFERENCE_CALLS).
 *   /square/command-center/communications/conference-calls
 *
 * The VIDEO-conferencing surface (Daily.co), owned by Communications. NOT the
 * internal Daily Meetings record (that stays under Operations). Owns the real
 * provider-status + rooms hooks and performs the REAL create / join / copy /
 * cancel flow:
 *   - create: POST /api/square/daily/meetings — server fail-closed without
 *     DAILY_API_KEY; a blocked create surfaces the server's exact reason.
 *   - join: navigates to the in-app Conference Room route; the scoped
 *     short-lived token is minted inside the room gate and held in memory only
 *     (never in a URL/query/storage/log/receipt/DB, never Daily chrome / a new tab).
 *   - copy: the backend-returned daily_room_url only (no token).
 *   - cancel: POST /meetings/cancel (confirmed in the surface; audited).
 * No secret ever reaches the browser.
 */
export default function SquareCodeConferenceCallsRoute() {
  const navigate = useNavigate()
  const status = useConferenceCallStatus()
  const calls = useConferenceCalls()
  return (
    <ConferenceCalls
      status={status.status}
      loading={status.loading}
      error={status.error}
      authRequired={status.authRequired}
      meetings={calls.meetings}
      meetingsLoading={calls.loading}
      meetingsError={calls.error}
      onCreate={async (input) => {
        const r = await createConferenceCall(input)
        if (r.ok) calls.reload()
        return r
      }}
      onJoin={async (meetingId) => {
        // In-app Conference Room: navigate to the AccessGate-protected room
        // route. The scoped short-lived join token is minted INSIDE the room
        // gate (POST /meetings/join) and held in memory only — never in this
        // URL, query, storage, log, receipt, or DB row, and Daily's own UI is
        // never opened in a new tab. Only display-only metadata rides on the
        // navigation state; the meeting id (non-secret) is the path param.
        const meeting = calls.meetings.find((m) => m.id === meetingId)
        navigate(conferenceRoomPath(meetingId), {
          state: {
            title: meeting?.title ?? 'Conference room',
            meetingMode: meeting?.meeting_mode,
            roomName: meeting?.daily_room_name ?? undefined,
            channelId: meeting?.context_type === 'comms_channel' ? meeting.context_id : null,
          },
        })
        return { ok: true }
      }}
      onCancel={async (meetingId) => {
        await cancelConferenceCall(meetingId, 'cancelled from Conference Calls')
        calls.reload()
      }}
      onCopyLink={(m: Meeting) => { void navigator.clipboard?.writeText(m.daily_room_url) }}
      onScheduleMeeting={() => navigate(commandCenterPath('operations/daily-meetings'))}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onNavTop={(to: string) => navigate(to)}
      onOpenMessageCenter={() => navigate(COMMS_ROOT)}
      onOpenTeam={() => navigate(TEAM_ROOT)}
      onOpenTasks={() => navigate(TEAM_ROOT)}
    />
  )
}
